<template>
  <ElDialog
    :title="$t('ADVERTISEMENT.BUDGET.CHARGE_CREDIT')"
    :visible="value"
    width="28rem"
    class="credit-charge-modal"
    @close="resetFormAndCloseModal"
  >
    <ElForm
      ref="inputCreditModal"
      :model="inputCreditModel"
      :rules="inputCreditRules"
      label-position="top"
      @submit.native.prevent="chargeCredit"
    >
      <ElFormItem
        :label="$t('ADVERTISEMENT.BUDGET.AMOUNT')"
        prop="creditAmount"
      >
        <label
          slot="label"
          v-dompurify-html="$t('ADVERTISEMENT.BUDGET.AMOUNT_WITH_DESCRIPTION')"
        />
        <div class="credit-charge-modal__shortcuts">
          <ElButton type="plain" @click="setCredit(5000000)">
            {{ $t('ADVERTISEMENT.BUDGET.SHORT_CUT_ONE') }}
          </ElButton>
          <ElButton type="plain" @click="setCredit(10000000)">
            {{ $t('ADVERTISEMENT.BUDGET.SHORT_CUT_TWO') }}
          </ElButton>
          <ElButton type="plain" @click="setCredit(450000000)">
            {{ $t('ADVERTISEMENT.BUDGET.SHORT_CUT_THREE') }}
          </ElButton>
        </div>
        <ElInput
          v-model.number="inputCreditModel.creditAmount"
          class="credit-charge-modal__input el-input-hide-numeric-counter"
          type="number"
          dir="ltr"
          :step="100000"
          :min="5000000"
          :max="450000000"
          :placeholder="$t('ADVERTISEMENT.BUDGET.BUDGET_PLACEHOLDER')"
          @blur="roundCreditAmount"
        />
        <InputHelper el-icon="el-icon-info">
          {{ $t('ADVERTISEMENT.BUDGET.MAX_CHARGE_AMOUNT_WARNING') }}
        </InputHelper>
      </ElFormItem>
      <div
        v-show="inputCreditModel.creditAmount"
        class="credit-charge-modal__report"
      >
        <div class="credit-charge-modal__report-item">
          <strong>{{ $t('ADVERTISEMENT.BUDGET.FINAL_CHARGE_AMOUNT') }}:</strong>
          {{ currencyFormat(roundedCreditAmount) }} {{ $t('RIALS') }}
        </div>
        <div class="credit-charge-modal__report-item">
          <strong>{{ $t('ADVERTISEMENT.BUDGET.TAX') }}:</strong>
          {{ currencyFormat(tax) }} {{ $t('RIALS') }}
        </div>
        <div class="credit-charge-modal__report-item">
          <strong>{{ $t('ADVERTISEMENT.BUDGET.TOTAL_CHARGE') }}:</strong>
          {{ currencyFormat(chargeAmount) }} {{ $t('RIALS') }}
        </div>
      </div>

      <!-- dirty hack until ElementUI ends up with a solution -->
      <input v-show="false" type="submit" />
    </ElForm>
    <div slot="footer" class="dialog-footer">
      <ElButton
        type="primary"
        :loading="isLoading"
        @click.native.prevent="chargeCredit"
      >
        {{ $t('ADVERTISEMENT.BUDGET.CHARGE_IT') }}
      </ElButton>
      <ElButton
        type="text"
        :plain="true"
        @click.native.prevent="resetFormAndCloseModal"
      >
        {{ $t('CANCEL') }}
      </ElButton>
    </div>
  </ElDialog>
</template>

<script>
import { AdFinance } from '@/api/api';
import { format as currencyFormat } from '@/utils/number';
import form from '@/mixins/form';
import modal from '@/mixins/modal';
import { TAX_PERCENT } from '@/constants/global-constants';
import InputHelper from '@@/common/InputHelper';

import {
  AD_FINANCE_BUDGET_INCREASE_REDIRECT_PAYMENT_STATE,
  STATE,
} from '@/constants/gtm-events';

export default {
  name: 'CreditChargeModal',
  components: {
    InputHelper,
  },
  mixins: [form, modal],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputCreditModel: {
        creditAmount: 5_000_000,
      },
      isLoading: false,
    };
  },
  computed: {
    roundedCreditAmount() {
      return 100_000 * Math.floor(this.inputCreditModel.creditAmount / 100_000);
    },
    tax() {
      return (TAX_PERCENT / 100) * this.roundedCreditAmount;
    },
    chargeAmount() {
      return this.roundedCreditAmount + this.tax;
    },
    inputCreditRules() {
      return {
        creditAmount: [
          {
            required: true,
            message: this.$t('VALIDATION_REQUIRED_ERR'),
            trigger: 'blur',
            type: 'number',
            min: 0,
          },
          {
            message: this.$t('ADVERTISEMENT.BUDGET.BUDGET_MIN_ERROR'),
            trigger: ['blur', 'change'],
            type: 'number',
            min: 5_000_000,
          },
          {
            message: this.$t('ADVERTISEMENT.BUDGET.BUDGET_MAX_ERROR'),
            trigger: ['blur', 'change'],
            type: 'number',
            max: 450_000_000,
          },
        ],
      };
    },
  },
  methods: {
    setCredit(credit) {
      this.inputCreditModel.creditAmount = credit;
    },
    resetFormAndCloseModal() {
      this.resetForm('inputCreditModal');
      this.$emit('input', false);
    },
    roundCreditAmount() {
      const enteredAmount = this.inputCreditModel.creditAmount;
      const roundedAmount = this.roundedCreditAmount;

      if (enteredAmount !== roundedAmount && roundedAmount) {
        this.setCredit(roundedAmount);
      }
    },
    chargeCredit() {
      if (!this.isValid('inputCreditModal')) return;

      this.isLoading = true;
      return AdFinance.getPaymentURL(this.roundedCreditAmount)
        .then(redirectUrl => {
          this.$gtm.trackEvent(
            AD_FINANCE_BUDGET_INCREASE_REDIRECT_PAYMENT_STATE(STATE.SUCCESS),
          );

          window.location.assign(redirectUrl);
        })
        .catch(({ message }) => {
          this.$gtm.trackEvent(
            AD_FINANCE_BUDGET_INCREASE_REDIRECT_PAYMENT_STATE(STATE.FAIL),
          );

          this.$message.error(message);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    currencyFormat,
  },
};
</script>

<style lang="scss">
.credit-charge-modal {
  &__shortcuts {
    margin-bottom: $global-margin;
  }

  &__report {
    &-item:not(:last-child) {
      margin-bottom: $global-margin / 2;
    }
  }

  &__input {
    input {
      text-align: center;
    }
  }
}
</style>
